import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, Heart, Briefcase, Star, BookOpen, Zap } from 'lucide-react';
import { analytics } from '../services/analytics';
import { useLanguage } from '../contexts/LanguageContext';
import { getH1 } from '../seo/seoRoutes.mjs';

const HomePage = () => {
  const [inspirationalQuestion, setInspirationalQuestion] = useState('');
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguage();

  useEffect(() => {
    const inspirationalQuestions = [
      t('inspire.question1'),
      t('inspire.question2'),
      t('inspire.question3'),
      t('inspire.question4'),
      t('inspire.question5'),
      t('inspire.question6'),
      t('inspire.question7'),
      t('inspire.question8'),
      t('inspire.question9'),
      t('inspire.question10')
    ];
    
    // Set a random inspirational question
    const randomQuestion = inspirationalQuestions[Math.floor(Math.random() * inspirationalQuestions.length)];
    setInspirationalQuestion(randomQuestion);
  }, [t]);

  // `slug` keys the list; `path` drives a real <a href> so crawlers can reach
  // every spread page. Routing used to be decided by comparing the *translated*
  // label against t('category.love') — a tautology (both sides were the same
  // call) that would have silently broken the moment either string changed.
  const readingCategories = [
    {
      slug: 'love',
      path: '/love-spread',
      name: t('category.love'),
      icon: Heart,
      color: "#ff6b8a",
      description: t('category.loveDesc'),
      questions: [t('questions.love1'), t('questions.love2'), t('questions.love3')]
    },
    {
      slug: 'career',
      path: '/career-spread',
      name: t('category.career'),
      icon: Briefcase,
      color: "#4ecdc4",
      description: t('category.careerDesc'),
      questions: [t('questions.career1'), t('questions.career2'), t('questions.career3')]
    },
    {
      slug: 'growth',
      path: '/personal-growth-spread',
      name: t('category.growth'),
      icon: Star,
      color: "#ffe066",
      description: t('category.growthDesc'),
      questions: [t('questions.growth1'), t('questions.growth2'), t('questions.growth3')]
    },
    {
      slug: 'health',
      path: '/mental-healing-spread',
      name: t('category.health'),
      icon: Zap,
      color: "#20B2AA",
      description: t('category.healthDesc'),
      questions: [t('questions.health1'), t('questions.health2'), t('questions.health3')]
    }
  ];

  return (
    <div className="home-page">
      {/* Welcome Section */}
      <section className="welcome-section">
        <div className="welcome-content">
          <div className="welcome-text">
            <h1 className="welcome-subtitle">
              {getH1('/', currentLanguage)}
            </h1>
            <p className="welcome-tagline">
              {t('home.subtitle')}
            </p>
            <blockquote className="inspiration-question">
              {inspirationalQuestion}
            </blockquote>
            <button 
              className="draw-card-btn"
              onClick={() => {
                // Track daily card draw
                analytics.trackSpreadSelection('Daily Card');
                navigate('/spread-drawing', { 
                  state: { 
                    spreadName: t('home.dailyCard'),
                    spreadType: 'single',
                    cardCount: 1,
                    spreadInterpretations: [t('dailyCard.position')],
                    question: inspirationalQuestion,
                    returnPath: "/",
                    spreadConfig: {
                      isSingleCard: true,
                      isDailyCard: true
                    }
                  } 
                });
              }}
            >
              <Sparkles size={20} />
              {t('home.drawCard')}
            </button>
          </div>
          <div className="welcome-decoration"></div>
        </div>
      </section>

      {/* Reading Categories */}
      <section className="categories-section">
        <div className="section-container">
          <h2 className="section-title">
            <Star className="section-icon" />
            {t('home.categoriesTitle')}
          </h2>
          <p className="section-description">
            {t('home.categoriesDescription')}
          </p>
          
          <div className="categories-grid">
            {readingCategories.map((category) => {
              const IconComponent = category.icon;
              return (
                <Link
                  key={category.slug}
                  to={category.path}
                  className="category-card"
                  style={{ '--category-color': category.color }}
                  /* Still the translated label, so historical GA rows for this
                     event stay comparable. */
                  onClick={() => analytics.trackSpreadSelection(category.name)}
                >
                  <div className="category-icon">
                    <IconComponent size={32} />
                  </div>
                  <h3 className="category-name">{category.name}</h3>
                  <p className="category-description">{category.description}</p>
                  <div className="category-questions">
                    <small>{t('home.sampleQuestions')}</small>
                    <ul>
                      {category.questions.slice(0, 2).map((question, qIndex) => (
                        <li key={qIndex}>{question}</li>
                      ))}
                    </ul>
                  </div>
                </Link>
              );
            })}
          </div>
          
          {/* General Reading Option */}
          <div className="general-reading-section">
            <div className="category-icon">
              <img 
                src="/images/taroca_favicon.png" 
                alt={t('alt.generalReading')}
                className="category-icon-img"
              width={64}
                  height={64}
                  loading="lazy"
                  decoding="async"
                />
            </div>
            <h3 className="general-reading-title">{t('home.notSureCategory')}</h3>
            <p className="general-reading-description">
              {t('home.generalReadingDesc')}
            </p>
            <Link
              to="/general-spread"
              className="general-reading-btn"
              onClick={() => analytics.trackSpreadSelection('General Reading')}
            >
              <Star size={20} />
              {t('home.createOwnReading')}
            </Link>
          </div>
        </div>
      </section>

      {/* Personal Growth Journey Section */}
      <section className="growth-section">
        <div className="section-container">
          <div className="growth-header">
            <p className="growth-subtitle">{t('home.whyTaroca')}</p>
            <h2 className="growth-title">{t('home.growthTitle')}</h2>
            <p className="growth-description">
              {t('home.growthDesc')}
            </p>
          </div>

          <div className="growth-content">
            <div className="growth-mixed-grid">
              {/* First Row */}
              <div className="tarot-card-image">
                <img 
                  src="/images/taroca_demo_v3.v2.webp" 
                  alt={t('alt.demoScreen')}
                  className="tarot-card-img"
                width={400}
                  height={667}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              
              <div className="feature-item">
                <div className="oracle-icon">
                  <img 
                    src="/images/icon_usp_2_magic.v2.webp" 
                    alt={t('alt.iconRitual')}
                    className="oracle-icon-img"
                  width={100}
                  height={100}
                  loading="lazy"
                  decoding="async"
                />
                </div>
                <p className="feature-category">{t('home.featureCategory1')}</p>
                <h3 className="feature-title">{t('home.featureTitle1')}</h3>
                <p className="feature-description">
                  {t('home.featureDesc1')}
                </p>
              </div>

              <div className="tarot-card-image">
                <img 
                  src="/images/tarot-cards/image_48.jpg" 
                  alt="Seven of Pentacles"
                  className="tarot-card-img"
                width={400}
                  height={667}
                  loading="lazy"
                  decoding="async"
                />
              </div>

              {/* Second Row */}
              <div className="feature-item">
                <div className="oracle-icon">
                  <img 
                    src="/images/icon_usp_1_see.v2.webp" 
                    alt={t('alt.iconInsight')}
                    className="oracle-icon-img"
                  width={100}
                  height={100}
                  loading="lazy"
                  decoding="async"
                />
                </div>
                <p className="feature-category">{t('home.featureCategory2')}</p>
                <h3 className="feature-title">{t('home.featureTitle2')}</h3>
                <p className="feature-description">
                  {t('home.featureDesc2')}
                </p>
              </div>

              <div className="tarot-card-image">
                <img 
                  src="/images/tarot-cards/image_17.jpg" 
                  alt="Seven of Swords"
                  className="tarot-card-img"
                width={400}
                  height={667}
                  loading="lazy"
                  decoding="async"
                />
              </div>

              <div className="feature-item">
                <div className="oracle-icon">
                  <img 
                    src="/images/icon_usp_3_vision.v2.webp" 
                    alt={t('alt.iconGrowth')}
                    className="oracle-icon-img"
                  width={100}
                  height={100}
                  loading="lazy"
                  decoding="async"
                />
                </div>
                <p className="feature-category">{t('home.featureCategory3')}</p>
                <h3 className="feature-title">{t('home.featureTitle3')}</h3>
                <p className="feature-description">
                  {t('home.featureDesc3')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tarot Diary Section */}
      <section className="diary-section">
        <div className="section-container">
          <h2 className="section-title">
            <BookOpen className="section-icon" />
            {t('home.diaryTitle')}
          </h2>
          <div className="diary-content">
            <div className="diary-description">
              <p>
                {t('home.diaryDesc')}
              </p>
              <div className="diary-features">
                <div className="feature">
                  <span className="feature-icon">📝</span>
                  <span>{t('home.diaryFeature1')}</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">🔍</span>
                  <span>{t('home.diaryFeature2')}</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">📈</span>
                  <span>{t('home.diaryFeature3')}</span>
                </div>
              </div>
            </div>
            <div className="diary-actions">
              <Link to="/diary" className="draw-card-btn diary-btn">
                <BookOpen size={20} />
                {t('home.viewDiary')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
